import { useEffect } from 'react'
import type { RefObject } from 'react'

type Root = RefObject<HTMLElement | null>

const prefersReducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches

/** 公開サイト表示中だけ、アンカーリンクのスムーススクロールを有効にする */
export const useSmoothScroll = () => {
  useEffect(() => {
    document.documentElement.classList.add('mp-site-active')
    return () => document.documentElement.classList.remove('mp-site-active')
  }, [])
}

/** ヘッダー：明るいセクションの上では白に切り替え */
export const useHeaderOnLight = (rootRef: Root, lightSelector: string) => {
  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    const hdr = root.querySelector<HTMLElement>('header')
    if (!hdr) return
    const lightSecs = root.querySelectorAll<HTMLElement>(lightSelector)
    const check = () => {
      const y = 54 / 2
      let light = false
      lightSecs.forEach(s => {
        const r = s.getBoundingClientRect()
        if (r.top <= y && r.bottom >= y) light = true
      })
      hdr.classList.toggle('on-light', light)
    }
    addEventListener('scroll', check, { passive: true })
    check()
    return () => removeEventListener('scroll', check)
  }, [rootRef, lightSelector])
}

/** 全幅写真のゆるやかなパララックス */
export const useParallax = (rootRef: Root) => {
  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    const imgs = [...root.querySelectorAll<HTMLImageElement>('img[data-parallax]')]
    if (!imgs.length || prefersReducedMotion()) return
    const par = () => {
      imgs.forEach(img => {
        const parent = img.parentElement
        if (!parent) return
        const r = parent.getBoundingClientRect()
        if (r.bottom < 0 || r.top > innerHeight) return
        const p = (r.top + r.height / 2 - innerHeight / 2) / innerHeight
        img.style.transform = `scale(1.08) translateY(${p * -26}px)`
      })
    }
    addEventListener('scroll', par, { passive: true })
    par()
    return () => removeEventListener('scroll', par)
  }, [rootRef])
}

/** マニフェスト：スクロールで言葉が灯る */
export const useManifesto = (rootRef: Root) => {
  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    const sec = root.querySelector<HTMLElement>('.manifesto')
    const tx = root.querySelector<HTMLElement>('.mtext')
    if (!sec || !tx) return
    const words = [...tx.querySelectorAll<HTMLElement>('.w')]
    if (prefersReducedMotion()) {
      words.forEach(w => w.classList.add('lit'))
      sec.classList.add('done')
      sec.style.height = 'auto'
      return
    }
    const upd = () => {
      const r = sec.getBoundingClientRect()
      const total = r.height - innerHeight
      const p = Math.min(1, Math.max(0, -r.top / Math.max(1, total)))
      const lit = Math.floor(p * 1.25 * words.length)
      words.forEach((w, i) => w.classList.toggle('lit', i < lit))
      sec.classList.toggle('done', p > 0.92)
    }
    addEventListener('scroll', upd, { passive: true })
    upd()
    return () => removeEventListener('scroll', upd)
  }, [rootRef])
}

/** ハイライトギャラリー：下スクロールでカードが切り替わる */
export const useScrollGallery = (rootRef: Root) => {
  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    const sec = root.querySelector<HTMLElement>('.gal')
    const track = root.querySelector<HTMLElement>('.gal-track')
    const sticky = root.querySelector<HTMLElement>('.gal-sticky')
    if (!sec || !track || !sticky) return
    const cards = [...track.querySelectorAll<HTMLElement>('.gal-card')]
    const tabs = [...root.querySelectorAll<HTMLElement>('.gal-tab')]
    const dots = [...root.querySelectorAll<HTMLElement>('.gal-dot')]
    const N = cards.length
    if (!N) return
    let cur = 0
    const mark = (i: number) => {
      if (i === cur) return
      cur = i
      cards.forEach((c, k) => c.classList.toggle('active', k === cur))
      tabs.forEach((t, k) => {
        t.classList.toggle('on', k === cur)
        t.setAttribute('aria-pressed', String(k === cur))
      })
      dots.forEach((d, k) => d.classList.toggle('on', k === cur))
    }
    const centerOf = (el: HTMLElement) => el.offsetLeft + el.offsetWidth / 2
    const cleanups: (() => void)[] = []
    const bind = (els: HTMLElement[], jump: (i: number) => void) => els.forEach((el, i) => {
      const h = () => jump(i)
      el.addEventListener('click', h)
      cleanups.push(() => el.removeEventListener('click', h))
    })

    if (prefersReducedMotion()) {
      // フォールバック：従来の手動スワイプ型
      sec.classList.add('free')
      const sync = () => {
        const mid = track.scrollLeft + track.clientWidth / 2
        let best = 0, bestD = Infinity
        cards.forEach((c, i) => {
          const d = Math.abs(centerOf(c) - mid)
          if (d < bestD) { bestD = d; best = i }
        })
        mark(best)
      }
      const onScroll = () => requestAnimationFrame(sync)
      track.addEventListener('scroll', onScroll, { passive: true })
      cleanups.push(() => track.removeEventListener('scroll', onScroll))
      bind(tabs, i => track.scrollTo({ left: centerOf(cards[i]) - track.clientWidth / 2 }))
      bind(dots, i => track.scrollTo({ left: centerOf(cards[i]) - track.clientWidth / 2 }))
      sync()
      return () => cleanups.forEach(fn => fn())
    }

    const apply = () => {
      const total = sec.offsetHeight - sticky.offsetHeight
      const p = Math.min(1, Math.max(0, -sec.getBoundingClientRect().top / Math.max(1, total)))
      const t = p * (N - 1)
      const i0 = Math.min(N - 1, Math.floor(t)), i1 = Math.min(N - 1, i0 + 1), f = t - i0
      const x = centerOf(cards[i0]) + (centerOf(cards[i1]) - centerOf(cards[i0])) * f - innerWidth / 2
      track.style.transform = `translateX(${-x}px)`
      mark(Math.round(t))
    }
    const jump = (i: number) => {
      const total = sec.offsetHeight - sticky.offsetHeight
      window.scrollTo({ top: sec.offsetTop + (i / (N - 1)) * total + 2, behavior: 'smooth' })
    }
    bind(tabs, jump)
    bind(dots, jump)
    const onScroll = () => requestAnimationFrame(apply)
    addEventListener('scroll', onScroll, { passive: true })
    addEventListener('resize', apply)
    apply()
    return () => {
      cleanups.forEach(fn => fn())
      removeEventListener('scroll', onScroll)
      removeEventListener('resize', apply)
    }
  }, [rootRef])
}

/** 数字カウントアップ */
export const useCountUp = (rootRef: Root) => {
  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    const cnts = root.querySelectorAll<HTMLElement>('.cnt')
    if (!cnts.length) return
    if (!('IntersectionObserver' in window) || prefersReducedMotion()) {
      cnts.forEach(c => { c.textContent = Number(c.dataset.to).toLocaleString() })
      return
    }
    const cio = new IntersectionObserver(es => es.forEach(e => {
      if (!e.isIntersecting) return
      cio.unobserve(e.target)
      const el = e.target as HTMLElement
      const to = Number(el.dataset.to), comma = el.dataset.comma, t0 = performance.now(), dur = 1400
      const tick = (t: number) => {
        const p = Math.min(1, (t - t0) / dur)
        const v = Math.round(to * (1 - Math.pow(1 - p, 3)))
        el.textContent = comma ? v.toLocaleString() : String(v)
        if (p < 1) requestAnimationFrame(tick)
      }
      requestAnimationFrame(tick)
    }), { threshold: .6 })
    cnts.forEach(c => cio.observe(c))
    return () => cio.disconnect()
  }, [rootRef])
}

/** スクロールでのフェードイン、特大スクショの出現、ステップ番号の点灯 */
export const useReveal = (rootRef: Root) => {
  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    const observers: IntersectionObserver[] = []
    const bigs = root.querySelectorAll<HTMLElement>('.shot-big,.lshot')
    const revealEls = root.querySelectorAll<HTMLElement>('.reveal,.stagger')

    if ('IntersectionObserver' in window) {
      const bio = new IntersectionObserver(es => es.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('in'); bio.unobserve(e.target) }
      }), { threshold: .25 })
      bigs.forEach(b => bio.observe(b))
      observers.push(bio)

      const io = new IntersectionObserver(es => es.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target) }
      }), { threshold: .12, rootMargin: '0px 0px -8% 0px' })
      revealEls.forEach(el => io.observe(el))
      observers.push(io)

      const litIO = new IntersectionObserver(es => {
        es.forEach(e => e.target.classList.toggle('lit', e.isIntersecting))
      }, { rootMargin: '-42% 0px -42% 0px' })
      root.querySelectorAll('.j-step').forEach(s => litIO.observe(s))
      observers.push(litIO)
    } else {
      bigs.forEach(b => b.classList.add('in'))
      revealEls.forEach(el => el.classList.add('in'))
    }

    // 安全装置：画面内に入っている要素は読み込み後に必ず表示する
    const safety = setTimeout(() => {
      revealEls.forEach(el => {
        if (el.getBoundingClientRect().top < innerHeight * 0.95) el.classList.add('in')
      })
    }, 600)

    return () => {
      observers.forEach(o => o.disconnect())
      clearTimeout(safety)
    }
  }, [rootRef])
}

/** スマホ固定CTA：CTAセクションが見えている間は隠す */
export const useStickyCta = (rootRef: Root) => {
  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    const sticky = root.querySelector<HTMLElement>('.sticky-cta')
    const contact = root.querySelector<HTMLElement>('.cta')
    if (!sticky || !contact || !('IntersectionObserver' in window)) return
    const io = new IntersectionObserver(es => {
      es.forEach(e => sticky.classList.toggle('hidden', e.isIntersecting))
    }, { threshold: .15 })
    io.observe(contact)
    return () => io.disconnect()
  }, [rootRef])
}
